
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { STORAGE_KEYS } from "../../utils/constants";
import { redirectToLogin } from "../../utils/apiClient";
import useScrollPosition from "../../hooks/useScrollPosition";

import CheckoutHeader from "../../components/servicing/checkout/CheckoutHeader";
import ProgressBar from "../../components/servicing/checkout/ProgressBar";
import AccountSection from "../../components/servicing/checkout/AccountSection";
import OrderSummary from "../../components/servicing/checkout/OrderSummary";
import OrderConfirmationModal from "../../components/servicing/checkout/OrderConfirmationModal";
import LocationSection from '../../components/servicing/checkout/LocationSection';
import {
  MapPinIcon,
  BuildingStorefrontIcon,
  TruckIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

const API_BASE_URL = (import.meta.env.VITE_BASE_URL || "http://localhost:8080").replace(/\/+$/, "");

const TIME_SLOTS = [
  "9:00 AM - 11:00 AM",
  "11:00 AM - 1:00 PM",
  "1:00 PM - 3:00 PM",
  "3:00 PM - 5:00 PM",
  "5:00 PM - 7:00 PM",
];

const DEFAULT_BOOKED = new Set(["1:00 PM - 3:00 PM"]);

const safeJson = async (resp) => {
  const text = await resp.text();
  try {
    return { data: JSON.parse(text), raw: text };
  } catch {
    return { data: null, raw: text };
  }
};

const getUserIdFromStorageUser = (currentUser) =>
  currentUser?.id ||
  currentUser?.user_id ||
  currentUser?.userid ||
  currentUser?.userId ||
  null;

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollPosition = useScrollPosition();

  const step2Ref = useRef(null);
  const step3Ref = useRef(null);
  const step4Ref = useRef(null);

  const [step, setStep] = useState(1);
  const [authChecked, setAuthChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Service type selection
const [serviceMode, setServiceMode] = useState('center'); 
const isDoorstep = serviceMode === "doorstep";

  // Cart + bike data from previous page
  const [cartItems, setCartItems] = useState([]);
  const [bikeData, setBikeData] = useState({
    brand: "Unknown",
    model: "Unknown",
    fuelType: "",
    image: "",
    brandId: null,
    modelId: null,
  });

  // Fuel type dropdown (required)
  const [fuelType, setFuelType] = useState("");



  const [userLocation, setUserLocation] = useState({
  lat: null,
  lng: null,
});

const [address, setAddress] = useState({
  flatNo: '',
  area: '',
  landmark: '',
  city: '',
  pincode: '',
  fullAddress: '',
});

const [locationError, setLocationError] = useState('');
  // Vehicle + notes
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [odometerKm, setOdometerKm] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");

  // Schedule
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");

  // Booked slots (demo)
  const [bookedSlots] = useState(DEFAULT_BOOKED);

  // Confirmation modal
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);

  // --- Pricing ---
  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + (Number(item.discountedPrice) || 0), 0),
    [cartItems]
  );

  const doorstepCharge = 100;
  const gst = useMemo(() => subtotal * 0.05, [subtotal]);

 const total = useMemo(
  () => subtotal+gst + (serviceMode === "doorstep" ? doorstepCharge : 0),
  [subtotal,gst, serviceMode]
);

  // Calendar limits
  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);
  const maxStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split("T")[0];
  }, []);

  // ---------------- AUTH ----------------
  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const userDataString = localStorage.getItem(STORAGE_KEYS.USER);

    if (token && userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        setIsLoggedIn(true);
        setCurrentUser(userData);
      } catch {
        setIsLoggedIn(false);
        setCurrentUser(null);
      }
    } else {
      setIsLoggedIn(false);
      setCurrentUser(null);
    }

    setAuthChecked(true);
  }, []);

  // ---------------- LOAD STATE ----------------
  useEffect(() => {
    window.scrollTo(0, 0);

    if (!location.state?.cartItems || !location.state?.bikeData) {
      alert("No items in cart. Redirecting to services page.");
      navigate("/service-packages");
      return;
    }

    setCartItems(location.state.cartItems || []);

    const incomingBike = location.state.bikeData || {};
    setBikeData(incomingBike);

    if (incomingBike?.fuelType && !fuelType) setFuelType(incomingBike.fuelType);
    if (!selectedDate) setSelectedDate(todayStr);
  }, [location.state, navigate, todayStr]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------- VALIDATION ----------------

  // ✅ Updated step1Valid — add locationOk check
const step1Valid = useMemo(() => {
  const scheduleOk  = !!selectedDate && !!selectedTimeSlot;
  const fuelOk      = !!fuelType;
  const locationOk  = !!(userLocation?.lat && userLocation?.lng);  // ← ADD THIS
  const odoOk       =
    odometerKm.trim().length === 0 ||
    (!isNaN(Number(odometerKm)) && Number(odometerKm) >= 0);

  if (!scheduleOk || !fuelOk || !odoOk || !locationOk) return false;

  if (isDoorstep) {
    return (
      address.flatNo.trim().length > 0 &&
      address.area.trim().length > 0 &&
      address.city.trim().length > 0 &&
      address.pincode.trim().length === 6 &&
      address.fullAddress.trim().length > 0
    );
  }

  return true;
}, [selectedDate, selectedTimeSlot, fuelType, odometerKm, isDoorstep, address, userLocation]);
  // ---------------- HANDLERS ----------------
  const handleAddressChange = (key, value) => setAddress((prev) => ({ ...prev, [key]: value }));

  const handleRemoveItem = (itemId) => {
    const updatedCart = cartItems.filter((item) => item.id !== itemId);
    setCartItems(updatedCart);
    if (updatedCart.length === 0) {
      alert("Cart is empty. Redirecting to services page.");
      navigate("/");
    }
  };

  const handleContinueFromStep1 = () => {
    if (!authChecked) return;
    if (!isLoggedIn) {
      redirectToLogin("Please login to continue with checkout");
      return;
    }
    if (!step1Valid) {
      alert("Please complete all required fields.");
      return;
    }
    setStep(2);
    setTimeout(() => step2Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handleContinueFromStep2 = () => {
    setStep(3);
    setTimeout(() => step3Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handleContinueFromStep3 = () => {
    setStep(4);
    setTimeout(() => step4Ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  // ---------------- CREATE BOOKING (COD) ----------------
  const handleCreateCashBooking = async () => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) {
      redirectToLogin("Please login to continue");
      return;
    }

    const userId = getUserIdFromStorageUser(currentUser);
    if (!userId) {
      alert("User info missing. Please login again.");
      return;
    }

    if (!cartItems.length) {
      alert("Your cart is empty.");
      return;
    }

    if (!selectedDate || !selectedTimeSlot) {
      alert("Please select date and time slot.");
      return;
    }

    if (!fuelType) {
      alert("Please select fuel type.");
      return;
    }

    // Backend requires: services: [{service_id, ...}] [file:19]
    const servicesPayload = cartItems.map((item) => ({
      service_id: Number(item.id),
      service_name: item.name || "Service",
      quantity: Number(item.quantity || 1),
      price: Number(item.discountedPrice || 0),
    }));

    const combinedAddress = isDoorstep
      ? [address.flatNo, address.area, address.landmark, address.city, address.pincode]
          .filter(Boolean)
          .join(", ")
      : "";

 // ❌ BROKEN payload references (undefined variables):
// selectedServiceTypeName, bikeDetails, totalPrice, paymentMethod, selectedServices

// ✅ FIXED payload using only variables that exist in this component:
const payload = {
  user_id:        userId,
  bike_brand:     bikeData?.brand || bikeData?.manufacturer || '',
  bike_model:     bikeData?.model || '',
  fuel_type:      fuelType,
  pincode:        address.pincode || '',
  service_type:   serviceMode === 'doorstep' ? 'Doorstep' : 'Center',
  issue_description: additionalNotes?.trim() || null,

  pickup_drop:       serviceMode === 'doorstep' ? 1 : 0,
  doorstep_flat_no:  isDoorstep ? address.flatNo    : null,
  doorstep_area:     isDoorstep ? address.area      : null,
  doorstep_landmark: isDoorstep ? address.landmark  : null,
  doorstep_city:     isDoorstep ? address.city      : null,
  doorstep_pincode:  isDoorstep ? address.pincode   : null,

  user_lat:  userLocation?.lat  || null,
  user_lng:  userLocation?.lng  || null,

  service_date:      selectedDate,
  service_time_slot: selectedTimeSlot,
  vehicle_number:    vehicleNumber?.trim() || null,
  kilometer:         odometerKm?.trim() ? Number(odometerKm) : null,
  engine_number:     null,
  chassis_number:    null,
  total_amount:      Number(total),
  payment_type:      'Cash',

  services: servicesPayload,   // already built above the payload block
};

    // IMPORTANT: correct route prefix is /api/booking (not /api/bookings) [file:1]
    const resp = await fetch(`${API_BASE_URL}/booking/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const { data, raw } = await safeJson(resp);

    // If your backend returns { success, booking_id } [file:19]
    if (!resp.ok || !data?.success) {
      const msg = data?.message || raw || `Server error ${resp.status}`;
      throw new Error(msg);
    }

    return data;
  };


  const validateLocationAndAddress = () => {
  if (!userLocation.lat || !userLocation.lng) {
    alert('Please select your location from current location, search, or map.');
    return false;
  }

  if (!address.flatNo.trim()) {
    alert('Please enter flat / house no.');
    return false;
  }

  if (!address.area.trim()) {
    alert('Please enter area / locality.');
    return false;
  }

  if (!address.city.trim()) {
    alert('Please enter city.');
    return false;
  }

  if (!address.pincode.trim()) {
    alert('Please enter pincode.');
    return false;
  }

  if (!address.fullAddress.trim()) {
    alert('Please enter full address.');
    return false;
  }

  return true;
};
// ✅ Fix handlePayment — add location guard at the top
const handlePayment = async () => {
  // Guard: location is required for both center and doorstep
  if (!userLocation?.lat || !userLocation?.lng) {
    alert("Please select your location on the map before placing a booking.");
    return;
  }

  // Guard: doorstep requires full address
  if (isDoorstep) {
    if (!address.flatNo.trim()) return alert("Please enter flat / house no.");
    if (!address.area.trim())   return alert("Please enter area / locality.");
    if (!address.city.trim())   return alert("Please enter city.");
    if (address.pincode.trim().length !== 6) return alert("Please enter a valid 6-digit pincode.");
    if (!address.fullAddress.trim()) return alert("Please enter full address.");
  }

  try {
    const result = await handleCreateCashBooking();
    setOrderDetails({
      orderId: result.booking_id || `BK${Date.now().toString().slice(-8)}`,
      cartItems,
      bikeDetails: {
        manufacturer: bikeData.brand || bikeData.manufacturer,
        model: bikeData.model,
        fuelType,
        vehicleNumber,
        kilometer: odometerKm,
        notes: additionalNotes,
      },
      selectedStore: isDoorstep ? "Doorstep Service" : "Service Center",
      selectedDate,
      selectedTimeSlot,
    });
    setShowOrderConfirmation(true);
  } catch (err) {
    console.error("BOOKING FAILED:", err);
    alert(`Failed to place booking: ${err.message}`);
  }
};
  // ---------------- UI ----------------
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-indigo-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="relative w-20 h-20">
            <div className="absolute inset-0 border-4 border-indigo-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-600 text-lg font-medium">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      {/* Scroll progress */}
      <div className="h-1 bg-gradient-to-r from-indigo-200 to-purple-200 fixed top-0 left-0 right-0 z-50">
        <div
          className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-300"
          style={{
            width: `${Math.min(
              (scrollPosition / (document.body.scrollHeight - window.innerHeight)) * 100,
              100
            )}%`,
          }}
        />
      </div>

      <CheckoutHeader />

      <div className="flex-grow flex flex-col lg:flex-row gap-6 px-4 sm:px-6 lg:px-8 py-6 mt-1 max-w-7xl mx-auto w-full">
        {/* Main */}
        <main className="flex-grow lg:w-2/3 order-1 space-y-6">
          <ProgressBar currentStep={step} />

          {/* Step 1 */}
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-200 space-y-6">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center">
                <MapPinIcon className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-gray-900">1) Service Location</h2>
                <p className="text-xs text-gray-500">Choose how you want service</p>
              </div>
            </div>

              <LocationSection
    serviceMode={serviceMode}
    setServiceMode={setServiceMode}
    userLocation={userLocation}
    setUserLocation={setUserLocation}
    address={address}
    setAddress={setAddress}
    locationError={locationError}
    setLocationError={setLocationError}
  />

            {/* Fuel type dropdown */}
            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-700 block">Fuel Type *</label>
              <select
                value={fuelType}
                onChange={(e) => setFuelType(e.target.value)}
                className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all text-sm bg-white"
              >
                <option value="">Select fuel type</option>
                <option value="Petrol">Petrol</option>
                <option value="Diesel">Diesel</option>
                <option value="Electric">Electric</option>
                <option value="CNG">CNG</option>
                <option value="Hybrid">Hybrid</option>
              </select>
              <p className="text-xs text-gray-500">This will be saved with booking details.</p>
            </div>

            {/* Vehicle + odometer */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-700">Vehicle Number (Optional)</label>
                <input
                  type="text"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value)}
                  placeholder="e.g., MH12AB1234"
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all text-sm"
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-700">Odometer Reading (km)</label>
                <input
                  type="text"
                  value={odometerKm}
                  onChange={(e) => setOdometerKm(e.target.value.replace(/\D/g, "").slice(0, 7))}
                  placeholder="e.g., 12500"
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all text-sm"
                />
              </div>
            </div>

            {/* Schedule */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-indigo-600" />
                <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                  2) Schedule Your Service
                </h3>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Preferred Date *</label>
                <input
                  type="date"
                  value={selectedDate}
                  min={todayStr}
                  max={maxStr}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all text-sm bg-white"
                />
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <ClockIcon className="h-5 w-5 text-indigo-600" />
                  Select Time Slot *
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {TIME_SLOTS.map((slot) => {
                    const isBooked = bookedSlots.has(slot);
                    const isSelected = selectedTimeSlot === slot;

                    return (
                      <button
                        key={slot}
                        onClick={() => !isBooked && setSelectedTimeSlot(slot)}
                        disabled={isBooked}
                        className={[
                          "px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all flex items-center justify-between",
                          isBooked
                            ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                            : isSelected
                            ? "border-green-600 bg-gradient-to-br from-green-600 to-emerald-600 text-white shadow-lg"
                            : "border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:shadow",
                        ].join(" ")}
                      >
                        <span>{slot}</span>
                        {isBooked ? (
                          <span className="text-xs font-bold">Booked</span>
                        ) : isSelected ? (
                          <CheckCircleIcon className="h-5 w-5" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="Any issues, pickup instructions, etc."
                  rows={3}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-200 transition-all text-sm resize-none"
                />
              </div>
            </div>

          
          </div>

          {/* Step 2 */}
          <AccountSection
            ref={step2Ref}
            step={step}
            isLoggedIn={isLoggedIn}
            currentUser={currentUser}
            redirectToLogin={redirectToLogin}
          />

          {/* Step 3 */}
          <div
            ref={step3Ref}
            className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-200 space-y-3"
          >
            <h2 className="text-base sm:text-lg font-bold text-gray-900">3) Review</h2>

            <p className="text-sm text-gray-700">
              Service Type:{" "}
              <span className="font-semibold">{isDoorstep ? "Doorstep Service" : "Service Center"}</span>
            </p>
            <p className="text-sm text-gray-700">
              Fuel Type: <span className="font-semibold">{fuelType}</span>
            </p>
            <p className="text-sm text-gray-700">
              Schedule:{" "}
              <span className="font-semibold">
                {selectedDate} / {selectedTimeSlot}
              </span>
            </p>

            {isDoorstep && (
              <p className="text-sm text-gray-700">
                Address:{" "}
                <span className="font-semibold">
                  {[address.flatNo, address.area, address.landmark, address.city, address.pincode]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </p>
            )}

            <button
              onClick={handleContinueFromStep3}
              className="w-full mt-2 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.01]"
            >
              Continue to Payment
            </button>
          </div>

          {/* Step 4 */}
          <div
            ref={step4Ref}
            className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border border-gray-200"
          >
            <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3">4) Payment</h2>
  {/* Payment Option: Cash on Delivery */}
      <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Cash on Delivery</h3>
            <p className="text-sm text-gray-600 mb-4">
              Pay with cash when your service is completed. This is the only payment method available currently.
            </p>
            <div className="flex items-center gap-2 text-green-700">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-semibold">Selected</span>
            </div>
          </div>
        </div>
      </div>
            <button
              onClick={handlePayment}
              className="w-full px-6 py-4 mt-6 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold text-lg rounded-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
            >
              <CheckCircleIcon className="h-6 w-6" />
              Place Booking
            </button>
          </div>
        </main>

        {/* Sidebar */}
        <aside className="lg:w-1/3 order-2">
          <OrderSummary
            cartItems={cartItems}
            bikeData={{
              manufacturer: bikeData.brand || bikeData.manufacturer,
              model: bikeData.model,
              fuelType: fuelType || bikeData.fuelType || "",
              image: bikeData.image,
            }}
            subtotal={subtotal}
             gst={gst}  
doorstepCharge={serviceMode === "doorstep" ? doorstepCharge : 0}
            total={total}
            handleRemoveItem={handleRemoveItem}
            currentStep={step}
            isLoggedIn={isLoggedIn}
            step1Valid={step1Valid}
            handleContinueStep1={handleContinueFromStep1}
            handleContinueStep2={handleContinueFromStep2}
          />

          {step === 3 && (
            <button
              onClick={handleContinueFromStep3}
              className="w-full mt-4 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              Continue to Payment
            </button>
          )}
        </aside>
      </div>


      <OrderConfirmationModal
        show={showOrderConfirmation}
        orderDetails={orderDetails}
        onOk={() => {
          setShowOrderConfirmation(false);
          navigate("/service-history");
        }}
        onClose={() => {
          setShowOrderConfirmation(false);
          navigate("/service-history");
        }}
      />
    </div>
  );
};

export default CheckoutPage;
